import { Box } from "@mui/material";
import { Button, Typography } from "@platform/design-system";

import useTierCarriers from "../../hooks/useTierCarriers";
import { SortCarrier, StrategicCarrier } from "../../types/rfpApi";
import AutocompleteTextField from "../autocompleteTextField";
import SelectedCarriers from "../selectedCarriers";

type Props = {
  carriers: string[];
  strategicCarriers: StrategicCarrier[];
  allCarriers: SortCarrier[];
  onDelete: (carrier: string) => void;
  onSelect: (carrier: string) => void;
};

function DialogContent({
  carriers,
  strategicCarriers,
  allCarriers,
  onDelete,
  onSelect,
}: Props): React.JSX.Element {
  const tierCarriers = useTierCarriers(strategicCarriers);

  return (
    <Box
      sx={{
        border: "1px solid",
        borderColor: "neutrals.neutral_300",
        borderRadius: 1,
      }}
    >
      <Typography
        variant="body-large"
        component="h4"
        sx={{ backgroundColor: "surfaces.primary", py: 1, px: 2 }}
      >
        <strong>Selected Carriers</strong>
      </Typography>
      <SelectedCarriers
        carriers={carriers}
        onDelete={onDelete}
        containerSx={{ p: 3, gap: 2 }}
      />

      {tierCarriers.map(({ title, tiers }) => (
        <Box key={title}>
          <Typography
            sx={{ backgroundColor: "neutrals.neutral_100", py: 1, px: 2 }}
          >
            <strong>{title}</strong>
          </Typography>
          <Box display="flex" sx={{ p: 3, gap: 2, flexWrap: "wrap" }}>
            {tiers.map((carrier) => (
              <Box key={carrier.id}>
                <Button
                  variant="tertiary"
                  disabled={carriers.includes(carrier.name)}
                  onClick={() => onSelect(carrier.name)}
                  sx={{ textAlign: "start" }}
                >
                  {carrier.name}
                </Button>
              </Box>
            ))}
          </Box>
        </Box>
      ))}

      <Box sx={{ px: 3, pb: 3 }}>
        <AutocompleteTextField
          placeholder="Search Carriers"
          fullWidth
          value={null}
          multiple={false}
          onChange={(_, carrier, reason) => {
            if (reason === "selectOption" && !carriers.includes(carrier.name)) {
              onSelect(carrier.name);
            }
          }}
          options={allCarriers}
          getOptionLabel={({ name }) => name}
          filterOptions={(options, { inputValue }) =>
            options.filter(({ name }) => {
              const nameLC = name.toLowerCase();
              const inputValueLC = inputValue.toLowerCase();
              return nameLC.startsWith(inputValueLC);
            })
          }
        />
      </Box>
    </Box>
  );
}

export { DialogContent };
